import React, { useState, useEffect } from 'react';
import { FaFileExcel, FaSearch } from 'react-icons/fa';
import UserTable from '../components/users/UserTable';
import BanModal from '../components/users/BanModal';
import UserAuditModal from '../components/users/UserAuditModal';
import { supabase } from '../services/supabase';
import adminService from '../services/admin.service';
import { useAuth } from '../context/AuthContext';

const exportToCSV = (rows, filename) => {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(','), ...rows.map(r => keys.map(k => `"${String(r[k] ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

function UsersPage() {
  const { admin, getAdminToken } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('local'); // 'local' | 'corporate'
  const [showBanModal, setShowBanModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [selectedUserForAudit, setSelectedUserForAudit] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [partnersMap, setPartnersMap] = useState({});

  useEffect(() => {
    fetchUsers();
    fetchPartners();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [searchTerm, filterStatus, activeTab, users, dateFrom, dateTo]);

  const fetchPartners = async () => {
    const { data } = await supabase.rpc('admin_list_partners', { p_admin_token: getAdminToken() });
    if (data?.success) {
      const map = {};
      data.rows.forEach(p => { map[p.id] = p.company_name; });
      setPartnersMap(map);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase.rpc('admin_list_users_full', { p_admin_token: getAdminToken() });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);

      const usersWithBanStatus = data.rows.map(u => ({
        ...u,
        is_banned: u.is_banned_legacy,
        ban_info: u.is_banned_legacy ? { reason: u.ban_reason_legacy, banned_until: u.ban_until_legacy } : null,
      }));

      setUsers(usersWithBanStatus);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Xəta baş verdi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Tab filter (local / corporate)
    filtered = filtered.filter((u) =>
      activeTab === 'corporate'
        ? u.user_type === 'corporate' || u.is_corporate === true
        : u.user_type === 'local' || u.user_type === 'tourist' || !u.user_type
    );

    // Search
    if (searchTerm) {
      filtered = filtered.filter(
        (u) =>
          u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter((u) => {
        if (filterStatus === 'active') return !u.is_banned;
        if (filterStatus === 'banned') return u.is_banned;
        return true;
      });
    }

    // Date range filter
    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      filtered = filtered.filter(u => new Date(u.created_at) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      filtered = filtered.filter(u => new Date(u.created_at) <= to);
    }

    setFilteredUsers(filtered);
  };

  const handleBan = (user) => {
    setSelectedUser(user);
    setShowBanModal(true);
  };

  const handleBanConfirm = async (userId, reason, duration) => {
    try {
      // Ban date hesabla
      let bannedUntil = null;
      if (duration === '7days') {
        bannedUntil = new Date();
        bannedUntil.setDate(bannedUntil.getDate() + 7);
      } else if (duration === '30days') {
        bannedUntil = new Date();
        bannedUntil.setDate(bannedUntil.getDate() + 30);
      }

      // Ban et
      const { data, error } = await supabase.rpc('admin_legacy_ban_user', {
        p_admin_token: getAdminToken(), p_user_id: userId, p_reason: reason, p_banned_until: bannedUntil?.toISOString() || null,
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);

      // Log yaz
      await adminService.logAction(admin.id, 'ban_user', 'user', userId, {
        reason,
        duration,
      });

      alert('User uğurla ban edildi!');
      setShowBanModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error banning user:', error);
      alert('Xəta baş verdi!');
    }
  };

  const handleUnban = async (user) => {
    if (!window.confirm(`${user.full_name} ban-ı ləğv etmək istəyirsiniz?`)) {
      return;
    }

    try {
      const { data, error } = await supabase.rpc('admin_legacy_unban_user', { p_admin_token: getAdminToken(), p_user_id: user.id });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);

      // Log yaz
      await adminService.logAction(admin.id, 'unban_user', 'user', user.id);

      alert('Ban uğurla ləğv edildi!');
      fetchUsers();
    } catch (error) {
      console.error('Error unbanning user:', error);
      alert('Xəta baş verdi!');
    }
  };

  const handleEditPoints = async (user) => {
    const newPoints = prompt(`${user.full_name} üçün yeni xal miqdarı:`, user.points || 0);

    if (newPoints === null) return;

    try {
      const { data, error } = await supabase.rpc('admin_update_user_points', {
        p_admin_token: getAdminToken(), p_user_id: user.id, p_points: parseInt(newPoints),
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);

      // Log yaz
      await adminService.logAction(admin.id, 'edit_points', 'user', user.id, {
        old_points: user.points,
        new_points: parseInt(newPoints),
      });

      alert('Xallar uğurla dəyişdirildi!');
      fetchUsers();
    } catch (error) {
      console.error('Error editing points:', error);
      alert('Xəta baş verdi!');
    }
  };

  const handleView = (user) => {
    setSelectedUserForAudit(user);
    setShowAuditModal(true);
  };

  const handleDelete = async (user) => {
    if (
      !window.confirm(
        `XƏBƏRDARLIQ: ${user.full_name} silinəcək! Bütün data silinəcək. Əminsiniz?`
      )
    ) {
      return;
    }

    if (!window.confirm('Son təsdiq! Geriyə dönüş yoxdur!')) {
      return;
    }

    try {
      const { data, error } = await supabase.rpc('admin_delete_user', {
        p_admin_token: getAdminToken(), p_user_id: user.id,
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);

      alert('User uğurla silindi!');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Xəta baş verdi: ' + error.message);
    }
  };

  // Checkbox helpers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredUsers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredUsers.map(u => u.id)));
    }
  };

  const handleBulkBan = async () => {
    if (!selectedIds.size) return;
    const reason = prompt('Ban səbəbi:');
    if (!reason) return;
    const items = filteredUsers.filter(u => selectedIds.has(u.id) && !u.is_banned);
    for (const user of items) {
      try {
        await supabase.rpc('admin_legacy_ban_user', {
          p_admin_token: getAdminToken(), p_user_id: user.id, p_reason: reason, p_banned_until: null,
        });
        await adminService.logAction(admin.id, 'ban_user', 'user', user.id, { reason, duration: 'permanent' });
      } catch (e) { console.error('Bulk ban error:', e); }
    }
    setSelectedIds(new Set());
    fetchUsers();
    alert(`${items.length} user ban edildi!`);
  };

  const handleBulkUnban = async () => {
    if (!selectedIds.size) return;
    const items = filteredUsers.filter(u => selectedIds.has(u.id) && u.is_banned);
    for (const user of items) {
      try {
        await supabase.rpc('admin_legacy_unban_user', { p_admin_token: getAdminToken(), p_user_id: user.id });
        await adminService.logAction(admin.id, 'unban_user', 'user', user.id);
      } catch (e) { console.error('Bulk unban error:', e); }
    }
    setSelectedIds(new Set());
    fetchUsers();
    alert(`${items.length} user-in banı ləğv edildi!`);
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Yüklənir...</p>
      </div>
    );
  }

  const localCount     = users.filter(u => u.user_type === 'local' || u.user_type === 'tourist' || !u.user_type).length;
  const corporateCount = users.filter(u => u.user_type === 'corporate' || u.is_corporate === true).length;
  const isCorporate = activeTab === 'corporate';

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>👥 İstifadəçi İdarəetməsi</h1>
          <p style={styles.subtitle}>Bütün istifadəçiləri idarə edin</p>
        </div>
        <button style={styles.btnExport} onClick={() => exportToCSV(filteredUsers.map(u => ({
          id: u.id, full_name: u.full_name, username: u.username, email: u.email,
          points: u.points,
          ...(isCorporate ? { corporate_partner_id: u.corporate_partner_id } : {}),
          total_checkins: u.total_checkins, is_banned: u.is_banned,
          created_at: u.created_at,
        })), `${activeTab}_users.csv`)}>
          <FaFileExcel /> Export Excel
        </button>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'local' ? styles.tabActive : {}) }}
          onClick={() => { setActiveTab('local'); setSelectedIds(new Set()); }}
        >
          🏠 Locallar <span style={styles.tabCount}>{localCount}</span>
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'corporate' ? styles.tabActiveCorporate : {}) }}
          onClick={() => { setActiveTab('corporate'); setSelectedIds(new Set()); }}
        >
          🏢 Korporativ <span style={styles.tabCount}>{corporateCount}</span>
        </button>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div style={styles.bulkBar}>
          <span style={styles.bulkInfo}>{selectedIds.size} seçildi</span>
          <button onClick={handleBulkBan} style={styles.bulkBan}>🚫 Hamısını Ban Et</button>
          <button onClick={handleBulkUnban} style={styles.bulkUnban}>✅ Hamısının Banını Ləğv Et</button>
          <button onClick={() => setSelectedIds(new Set())} style={styles.bulkClear}>Ləğv Et</button>
        </div>
      )}

      {/* Filters */}
      <div style={styles.filters}>
        <div style={styles.searchBox}>
          <FaSearch style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Ad, username və ya email ilə axtarış..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="all">Hamısı</option>
          <option value="active">Aktiv</option>
          <option value="banned">Ban edilmiş</option>
        </select>

        <div style={styles.dateRangeWrap}>
          <span style={styles.dateRangeLabel}>📅 Qeydiyyat tarixi:</span>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            style={styles.dateInput}
            title="Başlanğıc tarix"
          />
          <span style={{ color: '#999', fontSize: 13 }}>—</span>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            style={styles.dateInput}
            title="Son tarix"
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              style={styles.dateClearBtn}
              title="Tarixi sıfırla"
            >✕</button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={styles.stats}>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>{isCorporate ? 'Ümumi Korporativ:' : 'Ümumi Local:'}</span>
          <span style={styles.statValue}>{filteredUsers.length}</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Aktiv:</span>
          <span style={{ ...styles.statValue, color: '#4CAF50' }}>
            {filteredUsers.filter((u) => !u.is_banned).length}
          </span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Ban:</span>
          <span style={{ ...styles.statValue, color: '#F44336' }}>
            {filteredUsers.filter((u) => u.is_banned).length}
          </span>
        </div>
      </div>

      {/* Table */}
      <UserTable
        users={filteredUsers}
        onView={handleView}
        onBan={handleBan}
        onUnban={handleUnban}
        onEditPoints={handleEditPoints}
        onDelete={handleDelete}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        showPoints={true}
        isCorporate={isCorporate}
        partnersMap={partnersMap}
      />

      {/* Ban Modal */}
      {showBanModal && selectedUser && (
        <BanModal
          user={selectedUser}
          onConfirm={handleBanConfirm}
          onClose={() => {
            setShowBanModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {/* Audit Modal */}
      {showAuditModal && selectedUserForAudit && (
        <UserAuditModal
          user={selectedUserForAudit}
          onClose={() => {
            setShowAuditModal(false);
            setSelectedUserForAudit(null);
          }}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1800px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#999',
  },
  btnExport: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    borderBottom: '2px solid #E0E0E0',
    paddingBottom: '0',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 22px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#888',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '3px solid transparent',
    marginBottom: '-2px',
    cursor: 'pointer',
    borderRadius: '0',
    transition: 'all 0.2s',
  },
  tabActive: {
    color: '#FF6B35',
    borderBottomColor: '#FF6B35',
  },
  tabActiveCorporate: {
    color: '#8B5CF6',
    borderBottomColor: '#8B5CF6',
  },
  tabCount: {
    padding: '2px 8px',
    backgroundColor: '#F0F0F0',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '700',
    color: '#555',
  },
  filters: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  dateRangeWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 14px',
    backgroundColor: '#fff',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    flexWrap: 'wrap',
  },
  dateRangeLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#666',
    whiteSpace: 'nowrap',
  },
  dateInput: {
    padding: '6px 10px',
    fontSize: '13px',
    border: '1px solid #E0E0E0',
    borderRadius: '6px',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: '#F9F9F9',
    color: '#333',
  },
  dateClearBtn: {
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: '700',
    backgroundColor: '#F44336',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  searchBox: {
    flex: 1,
    minWidth: '300px',
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: '15px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#999',
  },
  searchInput: {
    width: '100%',
    padding: '12px 15px 12px 45px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
  },
  filterSelect: {
    padding: '12px 15px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: '#fff',
  },
  stats: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
  },
  statCard: {
    flex: 1,
    padding: '15px 20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statLabel: {
    fontSize: '14px',
    color: '#666',
    fontWeight: '600',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1A1A2E',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #FF6B35',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    backgroundColor: '#E3F2FD',
    borderRadius: '8px',
    marginBottom: '15px',
    flexWrap: 'wrap',
  },
  bulkInfo: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1A1A2E',
    flex: 1,
  },
  bulkBan: {
    padding: '8px 16px',
    backgroundColor: '#F44336',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  bulkUnban: {
    padding: '8px 16px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  bulkClear: {
    padding: '8px 16px',
    backgroundColor: '#9E9E9E',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default UsersPage;